import React, { useEffect, useState } from "react"
import shopPayLogo from "@svgs/shop-pay.svg"
import { getShopPayPlans } from "../../api/shop-pay"
import { useIsMobile } from "@src/utils/general"

const ShopPayBanner = ({ total }) => {
  const [lowestMonthly, setLowestMonthly] = useState(null)
  const [zeroAprAmount, setZeroAprAmount] = useState(null)
  const [installmentCount, setInstallmentCount] = useState(null)
  const [termCount, setTermCount] = useState(0)
  const [plans, setPlans] = useState([])
  const [showModal, setShowModal] = useState(false)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (!total) return

    const fetchPlans = async () => {
      try {
        const json = await getShopPayPlans()
        const pricing = json?.data?.shop?.shopPayInstallmentsPricing
        const allPlans = pricing?.financingPlans || []

        const outerMin = parseFloat(pricing?.minPrice?.amount || 0)
        const outerMax = parseFloat(pricing?.maxPrice?.amount || Infinity)
        const price = parseFloat(total)

        if (price < outerMin || price > outerMax) {
          setLowestMonthly(null)
          setZeroAprAmount(null)
          setInstallmentCount(null)
          setPlans([])
          setTermCount(0)
          return
        }

        const validPlans = allPlans.filter(p => {
          const min = parseFloat(p.minPrice?.amount || 0)
          const max = parseFloat(p.maxPrice?.amount || Infinity)
          return price >= min && price <= max
        })

        setPlans(validPlans)

        const allTerms = validPlans.flatMap(p =>
          p.terms.map(t => {
            const interestAmount = price * (t.apr / 100)
            const totalWithInterest = price + interestAmount
            return {
              ...t,
              monthly: totalWithInterest / t.installmentsCount.count,
            }
          })
        )

        setTermCount(allTerms.length)

        const cheapest = allTerms.sort((a, b) => a.monthly - b.monthly)[0]
        const zeroApr = allTerms.find(t => t.apr === 0)

        setLowestMonthly(cheapest?.monthly.toFixed(2) || null)
        setZeroAprAmount(zeroApr ? (price / zeroApr.installmentsCount.count).toFixed(2) : null)
        setInstallmentCount(zeroApr?.installmentsCount.count || null)
      } catch (error) {
        console.error("ShopPay fetch error:", error)
      }
    }

    fetchPlans()
  }, [total])

  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [showModal])

  return (
  <div>
    {lowestMonthly && (
      <p className="text-xs text-gray-600 flex flex-wrap items-center">
        {isMobile ? (
          <>
            <span className="text-gray-500">From</span>
            <span className="font-semibold ml-1">${lowestMonthly}</span>
            <span className="text-gray-500">/mo with </span>
          </>
        ) : installmentCount && termCount === 1 ? (
          <>
            <span className="text-gray-500">
              {installmentCount} interest-free installments of
            </span>
            <span className="font-semibold md:ml-1">${zeroAprAmount}</span>
            <span className="text-gray-500">/mo with </span>
          </>
        ) : (
          <>
            {installmentCount && (
              <span className="text-gray-500">
                {installmentCount} interest-free installments, or from
              </span>
            )}
            {!installmentCount && <span className="text-gray-500">From </span>}
            <span className="font-semibold md:ml-1">${lowestMonthly}</span>
            <span className="text-gray-500">/mo with </span>
          </>
        )}
        <img
          src={shopPayLogo}
          alt="Shop Pay"
          className={`inline align-text-bottom mx-1 ${isMobile ? "h-[14px]" : "h-4"}`}
        />
        <button
          onClick={() => setShowModal(true)}
          className="underline text-gray-500 hover:text-gray-700 md:ml-1"
        >
          View sample plans
        </button>
      </p>
    )}

    {showModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-lg p-6 mt-8 max-w-md ml-0 w-[85%] md:w-full shadow-lg max-h-[70vh] md:max-h-[100%] overflow-y-auto overflow-x-hidden relative">
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl leading-none"
            onClick={() => setShowModal(false)}
          >
            ×
          </button>
          <div className="w-full flex justify-center items-center mb-2">
            <img
              src={shopPayLogo}
              alt="Shop Pay"
              className="inline h-4 align-text-bottom mx-1"
            />
          </div>
          <h2 className="text-[16px] font-semibold flex items-center justify-center mb-0 h-[22px]">
            Sample Installment Plans
          </h2>
          <p className="text-sm text-gray-600 mb-4 flex items-center justify-center">
            Example payments for
            <strong className="ml-1">${parseFloat(total).toFixed(2)}</strong>
          </p>
          <div className="space-y-4">
            {plans.map((plan, i) =>
              plan.terms.map((term, j) => {
                const months = term.installmentsCount.count
                const apr = term.apr
                const interestAmount = (parseFloat(total) * (apr / 100)).toFixed(2)
                const totalAmount = (parseFloat(total) + parseFloat(interestAmount)).toFixed(2)
                const monthly = (totalAmount / months).toFixed(2)

                return (
                  <div
                    key={`${i}-${j}`}
                    className="border p-3 rounded-md bg-gray-50"
                  >
                    <p className="text-base font-semibold text-indigo-600">
                      ${monthly} <span className="text-sm">every month</span>
                    </p>
                    <p className="text-sm text-gray-600">Term: {months} months</p>
                    <p className="text-sm text-gray-600">Interest: {apr}% APR</p>
                    <p className="text-sm text-gray-600">
                      Total of payments: ${totalAmount}
                    </p>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    )}
  </div>
)

}

export default ShopPayBanner